"use client";

import { useState } from "react";
import "./datasap.css";
import DataSapReveal from "./DataSapReveal";
import {
  CashIcon,
  CloseIcon,
  PnLIcon,
  BuyIcon,
  StockIcon,
  SellIcon,
  MaintainIcon,
  PeopleIcon,
  PayrollIcon,
  ReportIcon,
  RiskIcon,
  SaveIcon,
  ReleaseIcon,
  GrowIcon,
  ProtectIcon,
  PlayIcon,
} from "./icons";

const valueCards = [
  [CashIcon, "Cash"], [CloseIcon, "Close"], [PnLIcon, "P&L"], [BuyIcon, "Buy"], [StockIcon, "Stock"],
  [SellIcon, "Sell"], [MaintainIcon, "Maintain"], [PeopleIcon, "People"], [PayrollIcon, "Payroll"], [ReportIcon, "Report"], [RiskIcon, "Risk"]
] as const;

const finderSteps = [
  { no: "1", title: "Find Enterprise Stock", text: "Search across all plants, stores and in-transit stock.", image: "/assets/images/high-inventory.jpg" },
  { no: "2", title: "Understand Criticality", text: "Factor in equipment criticality, safety stock and policy.", image: "/assets/images/understasnd-criticality.jpg" },
  { no: "3", title: "Transfer Safely", text: "Identify what can be transferred, where and when.", image: "/assets/images/transfer-safely.jpg" },
  { no: "4", title: "Take Action", text: "Create the transfer or purchase with confidence." }
];

const customers = [
  { name: "TATA POWER", logo: "/assets/images/tata.jpg" },
  { name: "Reliance", logo: "/assets/images/reliance.jpg" },
  { name: "adani", logo: "/assets/images/adani.jpg" },
  { name: "Mahindra", logo: "/assets/images/Mahindra-Logo-2012-1%201.jpg" },
  { name: "Coca-Cola", logo: "/assets/images/coca.jpg" },
  { name: "Emerson", logo: null },
  { name: "Dalmia", logo: "/assets/images/diama.jpg.jpg" },
  { name: "HAVELLS", logo: "/assets/images/havells.jpg" },
  { name: "L&T", logo: "/assets/images/lt.png" },
  { name: "JSW", logo: "/assets/images/jsw.jpg" },
];

function Arrow({ right = false }: { right?: boolean }) {
  return <span aria-hidden>{right ? "→" : "↗"}</span>;
}

export default function DataSap() {
  const [query, setQuery] = useState("");
  const [activeValue, setActiveValue] = useState("Cash");

  return (
    <main className="datapage">
      <DataSapReveal />

      <section className="datahero">
        <div className="dataheroImage" />
        <div className="dataheroShade" />
        <div className="dataheroContent">
          <h1>Do You Really Need<br />to Buy That Spare?</h1>
          <p className="dataheroLead">Your plant has zero. But your enterprise may already own it.</p>
          <p className="dataheroSub">Find it across your SAP. Understand criticality. Transfer safely.<br />Buy only when necessary.</p>
          <div className="dataheroButtons">
            <a href="#finder" className="fp-buttons fp-btn-primary dataprimaryBtn">See the Demo <Arrow right /></a>
            <a href="/tellusyourproblem#tell-details" className="fp-buttons fp-hero-outline-btn dataoutlineBtn">Book a Value Discovery</a>
          </div>
        </div>

        {/* <div className="datastockPanel">
          <div className="datapanelTop">
            <span>Material: <b>SP-10027</b></span>
            <strong>SAP</strong>
          </div>
          <div className="datastockRow">
            <span><i>!</i> Local Stock (Plant A)</span>
          </div>
          <div className="datastockRow">
            <span>Enterprise Stock</span>
            <b>7</b>
          </div>
          <div className="datastockRow">
            <span>Transferable Now</span>
            <b>1</b>
          </div>
          <div className="dataavoid">
            <span>Potential Purchase Avoidance</span>
            <strong>₹ 4.8 L</strong>
          </div>
          <button className="datapanelBtn">View Details</button>
        </div> */}
      </section>

      <section className="datavalueSection datareveal">
        <div className="datasectionHead">
          <h2>Explore Value Hiding in Your SAP</h2>
          <a href="#value">View All Value Finders <Arrow right /></a>
        </div>
        <div className="datavalueGrid datastagger">
          {valueCards.map(([Icon, label]) => (
            <button
              key={label}
              onClick={() => setActiveValue(label)}
              className={`datavalueCard ${activeValue === label ? "dataselected" : ""}`}
            >
              <span className="datavalueIcon"><Icon /></span><span>{label}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="datametrics datareveal">
        <div className="datametricsInner">
          <div><strong>100+</strong><span>Business Use Cases</span></div>
          <div><strong>6</strong><span>Core Business Domains</span></div>
          <div><strong>20+</strong><span>Years of SAP Expertise</span></div>
          <div><strong>Global</strong><span>Delivery & Support</span></div>
        </div>
      </section>

      <section className="datafinder datareveal" id="finder">
        <div className="datasectionHead">
          <div>
            <h2>This Week&apos;s Value Finder: Spare Intelligence</h2>
            <p>A 20-minute demo to show how SAP data can prevent unnecessary purchases and release working capital.</p>
          </div>
          <a href="#overview" className="datawatch"><PlayIcon /> Watch 90-Second Overview</a>
        </div>
        <div className="datafinderGrid datastagger">
          {finderSteps.map((step, i) => (
            <article className="datafinderCard" key={step.no}>
              <div className={`datamockScreen ${i === 3 ? "dataactionScreen" : "dataphotoScreen"}`}>
                {i === 0 ? (
                  <div className="datamockPhoto" style={{ backgroundImage: `url(${step.image})` }}>
                    <span className="dataplayOverlay"><PlayIcon /></span>
                  </div>
                ) : i < 3 ? (
                  <div className="datamockPhoto" style={{ backgroundImage: `url(${step.image})` }} />
                ) : (
                  <><div className="dataavoidBox">Avoid Unnecessary Purchase</div><div className="datamaterial">Material <b>SP-10027</b></div><div className="datasaving">Estimated Savings <b>₹ 4.8 L</b></div></>
                )}
              </div>
              <h3>{step.no}. {step.title}</h3>
              <p>{step.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="datafourWays datareveal">
        <h2>Four Ways We Help You Create Value</h2>
        <div className="datawaysGrid datastagger">
          <div className="dataway datasave"><span><SaveIcon /></span><div><b>SAVE</b><p>Reduce unnecessary spend and leakage.</p></div><Arrow right /></div>
          <div className="dataway datarelease"><span><ReleaseIcon /></span><div><b>RELEASE</b><p>Unlock trapped working capital.</p></div><Arrow right /></div>
          <div className="dataway datagrow"><span><GrowIcon /></span><div><b>GROW</b><p>Improve revenue and profitability.</p></div><Arrow right /></div>
          <div className="dataway dataprotect"><span><ProtectIcon /></span><div><b>PROTECT</b><p>Strengthen control, compliance and resilience.</p></div><Arrow right /></div>
        </div>
      </section>

      <section className="datatrusted datareveal">
        <div className="datasectionHead">
          <h2>Trusted by Leading Enterprises</h2>
          <a href="#stories">See More Customer Stories <Arrow right /></a>
        </div>
        <div className="datalogos datastagger">
          {customers.map((c) => (
            <div key={c.name} className="datacustomerLogo">
              {c.logo ? <img src={c.logo} alt={c.name} /> : c.name}
            </div>
          ))}
        </div>
        <div className="datatestimonial">
          <div className="dataavatar">AS</div>
          <div className="dataquote">“Orane helped us turn our SAP investment into measurable business value.<br />Their domain understanding and innovation mindset make them a true partner.”<small>CFO, Leading Manufacturing Company</small></div>
          <div className="dataslider">● ○ ○ ○ ○</div>
        </div>
      </section>

      <section className="datacta datareveal">
        <div className="datamountain" />
        <div className="datactaInner">
          <div className="datactaCopy">
            <h2>More Than Implementation.<br />A Smarter. More Valuable Tomorrow.</h2>
            <p>SAP Expertise × Applied AI × Business Outcomes</p>
          </div>
          {/* <div className="dataask">
            <h3>Ask Your SAP</h3>
            <div className="datasearchBox">
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="e.g. Where is my cash stuck?" />
              <button onClick={() => setQuery(query || "Show me value opportunities")}>→</button>
            </div>
            <div className="datachips">
              {["Can we close today?", "Why did margin fall?", "What looks unusual in payroll?"].map(x => <button key={x} onClick={() => setQuery(x)}>{x}</button>)}
            </div>
          </div> */}
        </div>
      </section>
    </main>
  );
}